import Decimal from "decimal.js";

import { TokenAmount } from "../types";

const MAX_DISPLAY_DECIMALS = 2;

export type FormatTokenOptions = {
    symbol?: string;
    hideSymbol?: boolean;
    short?: boolean;
    compactLargeNumbers?: boolean;
    displayDecimals?: number;
    signed?: boolean;
};

// Self-contained TokenAmount formatter: takes a raw atomic bigint + decimals and renders it. Owns
// its scaling and display logic so it never depends on the legacy number-based formatTokenAmount.
export function formatToken(amount: TokenAmount | undefined, options: FormatTokenOptions = {}): string {
    const symbol = options.symbol ?? "";
    const postFix = symbol && !options.hideSymbol ? ` ${symbol}` : "";

    const value = toUiDecimal(amount);
    if (value === undefined) return "--" + postFix;

    const prefix = options.signed && value.gt(0) ? "+" : "";
    const decimals = options.displayDecimals ?? MAX_DISPLAY_DECIMALS;
    const minVisible = new Decimal(1).div(new Decimal(10).pow(decimals));

    if (options.short && !value.isZero() && value.abs().lt(minVisible)) {
        return `${prefix}≤${minVisible.toString()}${postFix}`;
    }
    if (value.gt(0) && value.lt(1)) {
        return formatSmallTokenDecimals(value.toNumber()) + postFix;
    }
    if (options.compactLargeNumbers && value.abs().gt(10_000)) {
        return (
            prefix +
            new Intl.NumberFormat([], {
                notation: "compact",
                minimumSignificantDigits: 4,
                maximumSignificantDigits: 4
            }).format(value.toNumber()) +
            postFix
        );
    }
    return prefix + formatWithCommas(value, decimals) + postFix;
}

// Lossless raw bigint → UI Decimal: string-based scaling, never routes through Number.
function toUiDecimal(amount: TokenAmount | undefined): Decimal | undefined {
    if (!amount) return undefined;
    return new Decimal(amount.amount.toString()).div(new Decimal(10).pow(amount.decimals));
}

function formatWithCommas(decimal: Decimal, decimals: number): string {
    const fixed = decimal.toDecimalPlaces(decimals).toFixed(decimals);
    const [whole, fraction] = fixed.split(".");
    const sign = whole.startsWith("-") ? "-" : "";
    const wholeAbs = sign ? whole.slice(1) : whole;
    const wholeWithCommas = wholeAbs.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    const trimmedFraction = fraction?.replace(/0+$/, "");
    return `${sign}${wholeWithCommas}${trimmedFraction ? `.${trimmedFraction}` : ""}`;
}

// Collapses many leading fractional zeros into a subscript (e.g. 0.0₅123) for sub-1 amounts.
function formatSmallTokenDecimals(num: number): string {
    const rawFormat = num.toLocaleString([], { minimumSignificantDigits: 3, maximumSignificantDigits: 3 });
    const [whole, decimals] = rawFormat.split(".");
    if (!decimals) return rawFormat;

    let leadingZeros = 0;
    while (decimals[leadingZeros] === "0") leadingZeros++;

    if (leadingZeros < 3) return rawFormat;
    if (leadingZeros > 9) return "0";

    const subscript = String.fromCharCode(8320 + leadingZeros);
    return `${whole}.0${subscript}${decimals.slice(leadingZeros)}`;
}
